import React from 'react';
import {fromBackendDate} from '../../../../utils/DateUtils';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import Drawer from '../../../../components-new/ui/Drawer';

import config from 'config';
import {handleChange, handleValueChange} from 'utils/ComponentUtilFunctions';
import MemberConversationListManager from './chat-components/MemberConversationListManager';
import UserConversationListManager from './chat-components/UserConversationListManager';
import Conversation from './chat-components/Conversation';
import './index.css';
import './chat-css/Reactions.css'

import {
    clearSelectedConversation as clearSelectedConversationFromStore,
    closeForwardDialog as closeForwardDialogAction,
    forwardConversationRequested,
    insertConversation,
    loadOlderMessagesRequested,
    openForwardDialog as openForwardDialogAction,
    selectConversationRequested,
    sendChatMessageRequested,
} from '../../../../services/chat/action';
import LoadingComponent from '../../../../components-new/ui/LoadingComponent';
import {getOperatorHotkeysConfig, getUserByPlatform} from "../../../../services/company/api";
import SilverButton from "../../../../components-new/buttons/SilverButton";
import Volume2Icon from "../../../../assets/icons/Volume2Icon";
import VolumeOffIcon from "../../../../assets/icons/VolumeOffIcon";
import Colors from "../../../../utils/Colors";
import SearchInput from "../../../../components-new/form/SearchInput";
import PowerIcon from "../../../../assets/icons/PowerIcon";
import CloseChatsModal from "./chat-components/CloseChatsModal";
import HotkeysCheatSheet from "./chat-components/HotkeysCheatSheet";
import KeyboardIcon from "../../../../assets/icons/KeyboardIcon";
import {registerHotkeys} from "../../../../utils/hotkeyListener";
import {setUnreadBadge} from "../../../../utils/tabBadge";
import {isEnabled as desktopNotificationsEnabled, isSupported as desktopNotificationsSupported, setConversationOpener, setEnabled as setDesktopNotifications} from "../../../../utils/desktopNotifications";
import BellIcon from "../../../../assets/icons/BellIcon";
import BellOffIcon from "../../../../assets/icons/BellOffIcon";
import InfoIcon from "../../../../assets/icons/InfoIcon";
import Popup from "../../../../components-new/ui/Popup";
import Tooltip from "../../../../components-new/general/Tooltip";
import Translate, {t} from "../../../../components/Translate";
import ClockAlertIcon from "../../../../assets/icons/ClockAlertIcon";
import GalleryHorizontalEndIcon from "../../../../assets/icons/GalleryHorizontalEndIcon";
import BriefcaseBusinessIcon from "../../../../assets/icons/BriefcaseBusinessIcon";
import DestructiveButton from "../../../../components-new/buttons/DestructiveButton";

/* the key to the marks on a chat card, behind the info button beside the search */
const legendStyles = {
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    swatch: {
        flexShrink: 0,
        width: '12px',
        height: '12px',
        borderRadius: '3px',
        border: '1px solid var(--ds-border-secondary)',
    },
    /* keeps the icons of the other rows on the same line as the one with a fill */
    swatchSpacer: {
        flexShrink: 0,
        width: '12px',
    },
    text: {
        whiteSpace: 'nowrap',
        color: 'var(--ds-text-primary)',
    },
    note: {
        marginTop: '4px',
        paddingTop: '10px',
        borderTop: '1px solid var(--ds-border-secondary)',
        color: 'var(--ds-text-tertiary)',
        lineHeight: '16px',
    },
};

const styles = {
    /*
     * No padding of its own. The page it sits on - .dashboard-main - already
     * insets every screen by --ds-page-inset, and this added another 32px on
     * top, so the chat list started 52px from the panel while the history table
     * on the screen beside it starts at 20px. The gap between this column and
     * the conversation is set on .chat-panel.
     */
    drawerDocked: {
        height: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    searchBar: {
        height: '50px',
        backgroundColor: 'var(--ds-neutral-50)',
        position: 'relative',
        margin: '20px',
        borderRadius: '20px'
    },
    /*
     * Grey, like the conversation's own ground and its footer. White is kept for
     * one thing on this screen - the window the messages are in - so that it
     * reads as the thing being looked at and everything around it as furniture.
     */
    drawerPaper: {
        borderRadius: '10px',
        width: '360px',
        position: 'relative',
        height: '100%',
        border: '1px solid var(--ds-border-secondary)',
        boxSizing: 'border-box',
        background: 'var(--ds-bg-primary)',
    },
};

class Chat extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            temporaryDrawerOpen: false,
            contactsFilter: '',
            userData: {},
            filteredConversations: props.conversationsList || [],
            activeFilter: 'all',
            hotkeys: [],
            isSoundEnabled: true,
            isCloseChatsModalOpen: false,
            isHotkeysSheetOpen: false,
            desktopNotificationsOn: false,
        };
        this.handleChange = handleChange.bind(this);
        this.handleValueChange = handleValueChange.bind(this);
        /*
         * Both requests below outlive the screen: an operator who opens a chat
         * and leaves before the server answers used to get a state update on a
         * component that is no longer there. There is no way to cancel a fetch
         * that is already in flight, so the answer is dropped instead.
         */
        this.mounted = false;
    }

    setStateIfMounted = (state) => {
        if (this.mounted) this.setState(state);
    };

    componentDidMount() {
        this.mounted = true;

        const {company, user} = this.props;
        /* the screen can mount before the tariff has arrived */
        const features = (company && company.tariff && company.tariff.features) || [];

        if (features.includes(config.features.HOTKEYS)) {
            getOperatorHotkeysConfig()
                .then((hotkeys) => {
                    this.setStateIfMounted({hotkeys});

                    /*
                     * The screen-wide keys, as against the ones the header and
                     * the composer register for themselves. These four work with
                     * no chat open at all, which is why they are listened for
                     * here: the sheet is most wanted by someone who has not
                     * learned the rest yet, and moving between chats is how you
                     * get to one in the first place.
                     */
                    if (!this.mounted) return;

                    this.cleanupHotkeys = registerHotkeys(hotkeys, {
                        OPEN_HOTKEYS_HELP: () => this.toggleHotkeysSheet(),
                        NEXT_CHAT: () => this.stepThroughChats(1),
                        PREVIOUS_CHAT: () => this.stepThroughChats(-1),
                        FOCUS_MESSAGE_INPUT: () => this.focusMessageInput(),
                    });
                })
                .catch((error) => {
                    console.error("Error loading data", error);
                });
        }

        /*
         * Moved here from componentWillMount, which runs before the component is
         * on the screen at all - and is gone in React 17. Nothing reads userData
         * during the first render; it arrives when it arrives, exactly as before.
         */
        getUserByPlatform(user.id)
            .then((userData) => {
                this.setStateIfMounted({userData});
            })
            .catch((error) => {
                console.error("Error loading operator", error);
            });

        const stored = sessionStorage.getItem(`soundEnabled-${user.id}`);
        this.setState({
            isSoundEnabled: stored !== 'false',
            desktopNotificationsOn: desktopNotificationsEnabled(user.id),
        });

        /* a tab opened straight onto a list that already has unread chats */
        this.refreshUnreadBadge();

        this.cleanupNotificationOpener = setConversationOpener(this.openConversationFromNotification);
    }

    /*
     * A click on a system notification.
     *
     * Only a chat this operator has been put on is ever announced, so there is
     * one thing to do with the click: open it. No take - the chat is already
     * theirs - and the chat is checked against the operator again here rather
     * than taken on the notification's word.
     */
    openConversationFromNotification = (conversationId) => {
        const {conversationsList, conversation, history, match: {params: {id}}} = this.props;
        const {userData} = this.state;

        if (!userData.id) return;

        const target = conversationsList
            .find(item => String(item.id) === String(conversationId));

        if (!target || String(target.operatorId) !== String(userData.id)) return;
        if (String(conversation.id) === String(target.id)) return;

        /* the tab the chat is on, when it is not the one being looked at: the
           internal chats are a list of their own, and so is a single client */
        if (id === config.platformsMap.company
            || (id !== 'all' && String(target.companyId) !== String(id))) {
            history.push('/dashboard/chat/all');
        }

        this.selectConversation({
            ...target,
            fullConversationForSelect: undefined,
            isSupervisor: false,
        }, false, false);
    };

    /*
     * The number on the tab.
     *
     * Only the chats that are this operator's to answer: a supervisor watching
     * someone else's conversation is not being asked for anything, and a chat
     * in the queue belongs to whoever takes it first.
     */
    componentDidUpdate() {
        this.refreshUnreadBadge();

        const {user} = this.props;

        if (user && user.id && this.state.desktopNotificationsOn !== desktopNotificationsEnabled(user.id)) {
            /* the browser can take the permission away from under us */
            this.setState({desktopNotificationsOn: desktopNotificationsEnabled(user.id)});
        }
    }

    refreshUnreadBadge = () => {
        const {conversationsList} = this.props;
        const {userData} = this.state;

        const unread = conversationsList.reduce((total, conversation) => {
            const isInternal = conversation.platform === config.platformsMap.company;
            const isMine = userData.id
                && String(conversation.operatorId) === String(userData.id);

            if (!isInternal && !isMine) return total;

            return total + (conversation.unread || 0);
        }, 0);

        setUnreadBadge(unread);
    };

    componentWillUnmount() {
        this.mounted = false;
        if (this.cleanupHotkeys) this.cleanupHotkeys();
        if (this.cleanupNotificationOpener) this.cleanupNotificationOpener();
        /* the chat screen is where the count comes from, so it leaves with it */
        setUnreadBadge(0);
    }

    /*
     * Turning the system notifications on asks the browser the first time, and
     * the browser may say no - in which case nothing is stored and the operator
     * is told where the decision now lives.
     */
    toggleDesktopNotifications = () => {
        const {user, addNotification} = this.props;
        const wanted = !this.state.desktopNotificationsOn;

        setDesktopNotifications(user.id, wanted).then((granted) => {
            if (!this.mounted) return;

            this.setState({desktopNotificationsOn: granted});

            if (wanted && !granted) {
                addNotification('desktopNotificationsBlocked', config.notificationLevels.error);
            }
        });
    };

    toggleHotkeysSheet = () => {
        this.setState(prevState => ({isHotkeysSheetOpen: !prevState.isHotkeysSheetOpen}));
    };

    /*
     * The next chat the operator is already handling.
     *
     * Only their own, and only the ones they have taken: stepping into the
     * incoming list would take a chat off the queue on a keypress, which is a
     * decision, not a movement. The order is the one the list draws - most
     * recently active first - so the key goes where the eye would.
     */
    stepThroughChats = (step) => {
        const {conversationsList, conversation, match: {params: {id}}} = this.props;
        const {userData} = this.state;

        if (id === config.platformsMap.company || !userData.id) return;

        const own = conversationsList
            .filter(curConversation => curConversation.platform !== config.platformsMap.company
                && curConversation.status === config.conversationStatuses.operatorProcessing
                && curConversation.operatorId != null
                && String(curConversation.operatorId) === String(userData.id)
                && (id === 'all' || curConversation.companyId === id))
            .sort((a, b) => {
                const timeA = fromBackendDate(a.date ? a.date : a.lastModifiedAt).getTime();
                const timeB = fromBackendDate(b.date ? b.date : b.lastModifiedAt).getTime();
                return timeB - timeA;
            });

        if (own.length === 0) return;

        const current = own.findIndex(curConversation => curConversation.id === conversation.id);
        const target = own[current === -1 ? 0 : (current + step + own.length) % own.length];

        if (!target || target.id === conversation.id) return;

        /* exactly what clicking the card does for a chat already being handled */
        this.selectConversation({
            ...target,
            fullConversationForSelect: undefined,
            isSupervisor: false
        }, false, false);
    };

    /*
     * Back to typing. The field is reachable by mouse and by tab, and by
     * nothing else - every shortcut in the chat opens something, so the way
     * back out of it was the pointer.
     */
    focusMessageInput = () => {
        const field = document.getElementById('chat-message-input');
        if (!field) return;

        field.focus();
        field.selectionStart = field.selectionEnd = field.value.length;
    };

    toggleTemporaryDrawer = () => {
        const {temporaryDrawerOpen} = this.state;
        this.setState({
            temporaryDrawerOpen: !temporaryDrawerOpen,
        });
    };

    selectConversation = (conversation, silentMode = false, withTake = true) => {
        const {selectConversation} = this.props;
        this.setState({
            temporaryDrawerOpen: false,
        });
        selectConversation(conversation, silentMode, withTake);
    };

    filterMembersToForward = () => {
        const {conversation: {companyId}, company: {members}, user} = this.props;
        const {statusMap} = config;
        return members.filter(member =>
            member.id !== user.id && member.status === statusMap.enabled && (member.subCompanyIds.includes(companyId) || member.authorities[0] === config.rolesMap.admin || member.authorities[0] === config.rolesMap.owner));
    };

    componentWillReceiveProps(nextProps) {
        const {activeFilter} = this.state;

        let filteredConversations;
        if (activeFilter === 'all') {
            filteredConversations = nextProps.conversationsList;
        } else {
            filteredConversations = nextProps.conversationsList.filter(item => item.platform === activeFilter);
        }
        this.setState({filteredConversations});
    }

    toggleSound = () => {
        const {user} = this.props;
        const {isSoundEnabled} = this.state;
        const newValue = !isSoundEnabled;
        sessionStorage.setItem(`soundEnabled-${user.id}`, newValue);
        this.setState({isSoundEnabled: newValue});
    };

    openCloseChatsModal = () => {
        this.setState({
            isCloseChatsModalOpen: true,
        });
    };

    closeCloseChatsModal = () => {
        this.setState({
            isCloseChatsModalOpen: false,
        });
    };

    render() {
        const {
            conversation, sendChatMessage,
            forwardDialog, openForwardDialog, closeForwardDialog, forwardConversation,
            initializing, user, match: {params: {id}}, loadOlderMessages,
            clearSelectedConversationFromStore, company, addNotification
        } = this.props;

        const tariff = company.tariff
        const {features} = tariff
        const filteredMembers = this.filterMembersToForward();

        if (initializing) {
            return (
                <LoadingComponent containerStyles={{transform: 'translate(-33px, -33px)'}}/>
            );
        }

        const {
            temporaryDrawerOpen,
            contactsFilter,
            userData,
            filteredConversations,
            hotkeys,
            isSoundEnabled,
            isCloseChatsModalOpen,
            isHotkeysSheetOpen,
            desktopNotificationsOn
        } = this.state;


        function normalizeString(str) {
            if (typeof str !== 'string') return '';
            return str
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/\s+/g, "")
                .trim();
        }

        let filteredConversationList;
        if (contactsFilter && contactsFilter.trim() !== '') {
            const search = normalizeString(contactsFilter);

            filteredConversationList = filteredConversations.filter((conv) => {
                const fullName = normalizeString(conv.userFirstName + conv.userLastName);
                const reverseName = normalizeString(conv.userLastName + conv.userFirstName);

                return fullName.includes(search) || reverseName.includes(search);
            });
        } else {
            filteredConversationList = filteredConversations;
        }

        let ConversationListManager;
        if (id === config.platformsMap.company && features.includes(config.features.INTERNAL_CHATS)) {
            const conversations = filteredConversationList.filter(curConversation => curConversation.platform === config.platformsMap.company);
            ConversationListManager = (
                <MemberConversationListManager
                    conversations={conversations}
                    selectConversation={this.selectConversation}
                    selectedConversation={conversation}
                    insertConversation={this.props.insertConversation}
                    addNotification={addNotification}
                />
            );
        } else {
            const incomingConversations = filteredConversationList.filter(curConversation => curConversation.platform !== config.platformsMap.company
                && curConversation.status === config.conversationStatuses.unManaged && (id === 'all' || curConversation.companyId === id));

            const processingConversations = filteredConversationList.filter(curConversation => curConversation.platform !== config.platformsMap.company
                && curConversation.status === config.conversationStatuses.operatorProcessing && (id === 'all' || curConversation.companyId === id));

            ConversationListManager = (
                <UserConversationListManager
                    features={features}
                    forwardConversation={forwardConversation}
                    openForwardDialog={openForwardDialog}
                    incomingConversations={incomingConversations}
                    processingConversations={processingConversations}
                    selectConversation={this.selectConversation}
                    selectedConversation={conversation}
                    user={user}
                    userData={userData}
                    company={company}
                    loadOlderMessages={loadOlderMessages}
                    clearSelectedConversationFromStore={clearSelectedConversationFromStore}
                />
            );
        }

        const {subs: clients} = company;

        const drawer = (
            <div className="sidebar-content full-height flex-container-v" style={{overflow: 'hidden'}}>
                <div className="chat-sidebar-toolbar">
                    <div className="chat-sidebar-toolbar__actions">

                        {id !== config.platformsMap.company &&
                            <Popup
                                panelStyle={{
                                    fontSize: '11px',
                                    color: 'var(--ds-text-primary)',
                                    fontWeight: 'normal',
                                    minWidth: '230px'
                                }}
                                panelAlign={{horizontal: "right", vertical: ""}}
                                className="chat-sidebar-help"
                                style={{width: 'auto', height: '36px'}}
                                trigger={
                                    <Tooltip text="help" style={{display: 'flex'}}>
                                        <SilverButton
                                            type="button"
                                            width="36px"
                                            height="36px"
                                            iconOnly
                                        >
                                            <InfoIcon height={18} width={18} stroke="currentColor"/>
                                        </SilverButton>
                                    </Tooltip>
                                }
                            >
                                <div style={legendStyles.list}>
                                    {/*
                                      * What the marks on a card mean. The three
                                      * rows used to lead with a coloured square
                                      * each - two of them the same pale blue,
                                      * which was the fill every card had - so
                                      * the square said nothing. Only an away
                                      * chat is told by its fill now; the other
                                      * two are told by their icon, and the
                                      * legend shows exactly that.
                                      */}
                                    <div style={legendStyles.row}>
                                        <span style={{...legendStyles.swatch, background: Colors.danger50}}/>
                                        <ClockAlertIcon strokeWidth={3} style={{color: Colors.danger600}}
                                                        stroke="currentColor" width={12} height={12}/>
                                        <span style={legendStyles.text}><Translate text={"afkChat"}/></span>
                                    </div>

                                    <div style={legendStyles.row}>
                                        <span style={legendStyles.swatchSpacer}/>
                                        <GalleryHorizontalEndIcon strokeWidth={3} style={{color: Colors.neutral600}}
                                                                  stroke="currentColor" width={12} height={12}/>
                                        <span style={legendStyles.text}><Translate text={"inQueue"}/></span>
                                    </div>

                                    <div style={legendStyles.row}>
                                        <span style={legendStyles.swatchSpacer}/>
                                        <BriefcaseBusinessIcon strokeWidth={3} style={{color: Colors.neutral600}}
                                                               stroke="currentColor" width={12} height={12}/>
                                        <span style={legendStyles.text}><Translate
                                            text={"outOfWorkingHoursRequest"}/></span>
                                    </div>

                                    <div style={legendStyles.note}>
                                        {/* t returns undefined for a key the
                                            current language has not got, and
                                            .replace on that throws */}
                                        {(t({text: 'chatMessageLimitInfo', user}) || '')
                                            .replace("__PARAM1__", config.messagesPageDefaultSize)}
                                    </div>

                                </div>
                            </Popup>
                        }

                        {id !== config.platformsMap.company &&
                            <Tooltip
                                text={isSoundEnabled ? 'mute' : 'unmute'}
                                style={{display: 'flex'}}
                            >
                                <SilverButton
                                    type="button"
                                    width="36px"
                                    height="36px"
                                    iconOnly
                                    onClick={this.toggleSound}
                                >
                                    {isSoundEnabled
                                        ? <Volume2Icon height={18} width={18} stroke="currentColor"/>
                                        : <VolumeOffIcon height={18} width={18} stroke="currentColor"/>}
                                </SilverButton>
                            </Tooltip>
                        }

                        {id !== config.platformsMap.company && desktopNotificationsSupported() &&
                            <Tooltip
                                text={desktopNotificationsOn
                                    ? 'disableDesktopNotifications'
                                    : 'enableDesktopNotifications'}
                                style={{display: 'flex'}}
                            >
                                <SilverButton
                                    type="button"
                                    width="36px"
                                    height="36px"
                                    iconOnly
                                    onClick={this.toggleDesktopNotifications}
                                >
                                    {desktopNotificationsOn
                                        ? <BellIcon height={18} width={18} stroke="currentColor"/>
                                        : <BellOffIcon height={18} width={18} stroke="currentColor"/>}
                                </SilverButton>
                            </Tooltip>
                        }

                        {/* not in the internal chats, where all but one of these
                            keys does nothing - the header registers none of its
                            own for a chat between colleagues */}
                        {id !== config.platformsMap.company
                            && features.includes(config.features.HOTKEYS) && hotkeys.length > 0 &&
                            <SilverButton
                                type="button"
                                width="auto"
                                height="36px"
                                onClick={this.toggleHotkeysSheet}
                            >
                                <KeyboardIcon height={18} width={18} stroke="currentColor"/>
                                <span className="chat-sidebar-toolbar__action-label">
                                    <Translate text="hotkeys"/>
                                </span>
                            </SilverButton>
                        }

                        {(user.authorities.includes(config.rolesMap.owner) || user.authorities.includes(config.rolesMap.admin))
                            && features.includes(config.features.END_UNMANAGED_CHATS)
                            && id !== config.platformsMap.company &&
                            <DestructiveButton
                                type="button"
                                variant="outline"
                                size="sm"
                                width="auto"
                                onClick={this.openCloseChatsModal}
                            >
                                <PowerIcon height={18} width={18} stroke="currentColor"/>
                                <span className="chat-sidebar-toolbar__action-label">
                                    <Translate text="endChats"/>
                                </span>
                            </DestructiveButton>
                        }

                    </div>

                    <div className="chat-sidebar-toolbar__search">
                        <SearchInput
                            onChange={e => this.handleValueChange('contactsFilter')(e.target.value)}
                            value={contactsFilter}
                            id="chat-contact-search-input"
                            containerStyle={{
                                height: '36px',
                                width: '100%'
                            }}
                            style={{
                                height: '36px'
                            }}
                        />
                    </div>
                </div>

                {ConversationListManager}
            </div>
        );

        return (
            <div className="chat-panel">
                <div style={{zIndex: '1'}} className="lg-hidden-down">
                    <div
                        style={{
                            ...styles.drawerDocked
                        }}
                        className="fade-in"
                    >
                        <div style={styles.drawerPaper}>
                            {drawer}
                        </div>
                    </div>
                </div>
                <div className="lg-hidden-up">
                    <Drawer
                        variant="temporary"
                        open={temporaryDrawerOpen}
                        onClose={this.toggleTemporaryDrawer}
                        width={400}
                        className="chat-select-drawer"
                        overlayClassName="chat-select-drawer-overlay"
                    >
                        {drawer}
                    </Drawer>
                </div>
                <div style={{display: 'flex', height: '100%', width: '100%', flexDirection: 'column', minWidth: 0}}>
                    <div className="conversation-wrapper">
                        <Conversation
                            fromUnmanagedList={conversation.fullConversationForSelect !== undefined}
                            readOnly={conversation.fullConversationForSelect !== undefined} // only for unmanaged list
                            selectConversation={this.selectConversation}
                            company={company}
                            conversation={conversation}
                            sendChatMessage={sendChatMessage}
                            openForwardDialog={openForwardDialog}
                            openContactList={this.toggleTemporaryDrawer}
                            loadOlderMessages={loadOlderMessages}
                            user={user}
                            onUnMount={clearSelectedConversationFromStore}
                            hasBackButton
                            filteredMembers={filteredMembers}
                            forwardDialog={forwardDialog}
                            close={closeForwardDialog}
                            forwardConversation={forwardConversation}
                            tariff={company.tariff}
                            hotkeys={hotkeys}
                        />
                    </div>
                </div>

                {isHotkeysSheetOpen &&
                    <HotkeysCheatSheet
                        isOpen={isHotkeysSheetOpen}
                        onRequestClose={this.toggleHotkeysSheet}
                        hotkeys={hotkeys}
                        features={features}
                    />
                }

                {isCloseChatsModalOpen &&
                    <CloseChatsModal
                        user={user}
                        clients={clients}
                        company={company}
                        addNotification={addNotification}
                        isOpen={isCloseChatsModalOpen}
                        onRequestClose={this.closeCloseChatsModal}
                    />
                }

            </div>
        );
    }
}


Chat.propTypes = {
    user: PropTypes.object.isRequired,
    company: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    classes: PropTypes.object,
    conversation: PropTypes.object.isRequired,
    conversationsList: PropTypes.array.isRequired,
    initializing: PropTypes.bool.isRequired,
    selectConversation: PropTypes.func.isRequired,
    sendChatMessage: PropTypes.func.isRequired,
    forwardDialog: PropTypes.object.isRequired,
    openForwardDialog: PropTypes.func.isRequired,
    closeForwardDialog: PropTypes.func.isRequired,
    forwardConversation: PropTypes.func.isRequired,
    loadOlderMessages: PropTypes.func.isRequired,
    clearSelectedConversationFromStore: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
    user: state.user,
    chat: state.chat,
    company: state.company,
    conversationsList: state.chat.conversationsList,
    conversation: state.chat.conversation,
    forwardDialog: state.chat.forwardDialog,
    initializing: state.chat.initializing,
});

const mapDispatchToProps = dispatch => ({
    selectConversation: (conversation, silentMode = false, withTake = true) => {
        dispatch(selectConversationRequested(conversation, silentMode, withTake));
    },
    sendChatMessage: (message, commentMessage) => {
        dispatch(sendChatMessageRequested(message, commentMessage));
    },
    openForwardDialog: () => {
        dispatch(openForwardDialogAction());
    },
    closeForwardDialog: () => {
        dispatch(closeForwardDialogAction());
    },
    forwardConversation: (operatorContact, silentMode, conv) => {
        dispatch(forwardConversationRequested(operatorContact, silentMode, conv));
    },
    loadOlderMessages: () => {
        dispatch(loadOlderMessagesRequested());
    },
    clearSelectedConversationFromStore: () => {
        dispatch(clearSelectedConversationFromStore());
    },
    insertConversation: (conversation) => {
        dispatch(insertConversation(conversation));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(Chat);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/index.jsx